// Texto para el agente a partir de los errores de los clientes de incidencias,
// paquetes, búsqueda por teléfono y nota de sistema (vista "Atención e
// incidencias"). Se decide por `code` y status, nunca por el `message` del
// backend: viene en voseo (la web usa tuteo) o en inglés, y puede filtrar
// detalles internos. Única excepción: el 400 de validación del alta, que es lo
// que el agente tiene que corregir en el formulario.
//
// Acepta las formas que devuelven esos clientes: el errorInfo de errorHandler
// (getAllIncidents, getPackageIncident(s), createPackageIncident), el body que
// resuelve getAdminPackages en `{ error }` (undefined si no hubo respuesta) y
// los errores reducidos de searchPackagesByReceiverPhones y
// postConversationSystemNote.

import {
  apiBodyMessage,
  apiErrorCode,
  apiErrorData,
  apiErrorStatus,
} from './apiErrorMessage';

// Códigos del backend (microservice-packages, microservice-conversations y BFF).
// El 429 se resuelve por status, porque el throttler global de packages
// responde sin `code` (PHONE_SEARCH_RATE_LIMITED es solo el del rate limit
// propio), y PHONE_SEARCH_FAILED es un 500 que cae en la rama de 5xx.
export const INCIDENTS_FORBIDDEN_ROLE = 'INCIDENTS_FORBIDDEN_ROLE';
export const INCIDENTS_SCOPE_UNRESOLVED = 'INCIDENTS_SCOPE_UNRESOLVED';
export const PACKAGES_SCOPE_UNRESOLVED = 'PACKAGES_SCOPE_UNRESOLVED';
export const INCIDENT_IDEMPOTENCY_KEY_REUSED = 'INCIDENT_IDEMPOTENCY_KEY_REUSED';
export const PHONE_SEARCH_INVALID_PHONE = 'PHONE_SEARCH_INVALID_PHONE';
export const PHONE_SEARCH_INVALID_WINDOW = 'PHONE_SEARCH_INVALID_WINDOW';
export const PHONE_SEARCH_OPERATOR_REQUIRED = 'PHONE_SEARCH_OPERATOR_REQUIRED';
export const PHONE_SEARCH_TIMEOUT = 'PHONE_SEARCH_TIMEOUT';
export const BACKOFFICE_ONLY = 'backoffice_only';

/** Qué operación falló: el mismo status significa cosas distintas en cada una. */
export const INCIDENTS_ERROR_SOURCE = Object.freeze({
  incidents: 'incidents', // listados de incidencias
  packages: 'packages', // listados de paquetes (GET /packages)
  create: 'create', // alta de una incidencia
  phoneSearch: 'phoneSearch', // búsqueda de paquetes por teléfono
  systemNote: 'systemNote', // nota de sistema en el chat
});

// Ventana máxima de la búsqueda por teléfono si el body no trae `maxDays`.
const DEFAULT_PHONE_SEARCH_MAX_DAYS = 90;

/** Segundos de espera del 429, del error reducido o del body; null si no hay. */
function retryAfterSecondsOf(error) {
  const value = error?.retryAfterSeconds ?? apiErrorData(error)?.retryAfterSeconds;
  return Number.isFinite(value) && value > 0 ? Math.ceil(value) : null;
}

function forbiddenText(code, t) {
  if (code === INCIDENTS_FORBIDDEN_ROLE) return t('Tu rol no tiene acceso a las incidencias.');
  if (code === INCIDENTS_SCOPE_UNRESOLVED || code === PACKAGES_SCOPE_UNRESOLVED) {
    return t('No pudimos identificar tu operador. Cierra sesión y vuelve a entrar; si sigue, avisa a soporte.');
  }
  if (code === BACKOFFICE_ONLY) return t('Tu rol no tiene acceso a la mesa de ayuda.');
  return t('No tienes permisos para realizar esta acción.');
}

function createText(status, code, error, t) {
  // Sin respuesta o con un 5xx no se sabe si la incidencia quedó guardada; la
  // misma clave de idempotencia hace seguro reintentar.
  if (status === null || status >= 500) {
    return t("No pudimos confirmar si se creó. Revisa 'Incidencias de este pedido' o reintenta: no se duplicará.");
  }
  if (status === 404) return t('Este pedido no existe o no es de tu operación.');
  if (code === INCIDENT_IDEMPOTENCY_KEY_REUSED) {
    return t('No pudimos confirmar la incidencia. Revisa las incidencias de este pedido antes de volver a crearla.');
  }
  if (status === 400 && !code) {
    return (
      apiBodyMessage(apiErrorData(error)) ??
      t('Revisa los datos de la incidencia e inténtalo de nuevo.')
    );
  }
  return t('No se pudo crear la incidencia. Tus datos siguen en el formulario: inténtalo de nuevo.');
}

/** Errores propios de la búsqueda por teléfono; null si no aplica ninguno. */
function phoneSearchText(status, code, error, t, phones) {
  if (code === PHONE_SEARCH_TIMEOUT) {
    return t('La búsqueda tardó demasiado. Acota el período e inténtalo de nuevo.');
  }
  if (code === PHONE_SEARCH_INVALID_PHONE) {
    const invalidIndexes = apiErrorData(error)?.invalidIndexes;
    const value = Array.isArray(invalidIndexes) ? phones?.[invalidIndexes[0]] : null;
    if (typeof value === 'string' && value.trim()) {
      return t('{{valor}} no parece un teléfono.', { valor: value.trim() });
    }
    return t('Revisa los teléfonos y el período e inténtalo de nuevo.');
  }
  if (code === PHONE_SEARCH_INVALID_WINDOW) {
    const maxDays = apiErrorData(error)?.maxDays;
    return t('El período puede ser de hasta {{dias}} días. Acótalo e inténtalo de nuevo.', {
      dias: Number.isFinite(maxDays) ? maxDays : DEFAULT_PHONE_SEARCH_MAX_DAYS,
    });
  }
  if (code === PHONE_SEARCH_OPERATOR_REQUIRED) return t('Elige un operador para buscar por teléfono.');
  // Un 400 sin code es el ValidationPipe: su texto no se muestra.
  if (status === 400 && !code) return t('Revisa los teléfonos y el período e inténtalo de nuevo.');
  return null;
}

/**
 * Texto del error para mostrar en línea.
 * @param {unknown} error - error devuelto por el cliente (ver cabecera)
 * @param {(key: string, options?: object) => string} t
 * @param {{source?: string, phones?: string[]}} [options] - `source`, uno de
 *   INCIDENTS_ERROR_SOURCE (por defecto `incidents`); `phones`, los teléfonos
 *   buscados, para nombrar el inválido en PHONE_SEARCH_INVALID_PHONE
 * @returns {string|null} null solo para una request cancelada por el llamador,
 *   que no es un error para el agente
 */
export function incidentsApiErrorText(error, t, { source = INCIDENTS_ERROR_SOURCE.incidents, phones } = {}) {
  if (error?.canceled === true) return null;

  // La incidencia ya existe: lo que hay que decir es que falta la nota, sea cual
  // sea la causa. Si la ruta no existe, el llamador lo sabe por `unavailable`.
  if (source === INCIDENTS_ERROR_SOURCE.systemNote) {
    return t('La incidencia se creó, pero no pudimos dejar la nota en el chat.');
  }

  const status = apiErrorStatus(error);
  const code = apiErrorCode(error);

  if (status === 403) return forbiddenText(code, t);
  if (source === INCIDENTS_ERROR_SOURCE.create) return createText(status, code, error, t);

  if (status === 429) {
    const seconds = retryAfterSecondsOf(error);
    return seconds
      ? t('Hiciste muchas búsquedas seguidas. Espera {{segundos}} s e inténtalo de nuevo.', { segundos: seconds })
      : t('Hiciste muchas búsquedas seguidas. Espera unos segundos e inténtalo de nuevo.');
  }

  if (source === INCIDENTS_ERROR_SOURCE.phoneSearch) {
    const text = phoneSearchText(status, code, error, t, phones);
    if (text) return text;
  }

  if (status !== null && status >= 500) return t('El servicio no respondió a tiempo. Inténtalo de nuevo.');

  const isPackages =
    source === INCIDENTS_ERROR_SOURCE.packages || source === INCIDENTS_ERROR_SOURCE.phoneSearch;
  if (status === null) {
    return isPackages
      ? t('No pudimos cargar los paquetes. Revisa tu conexión e inténtalo de nuevo.')
      : t('No pudimos cargar las incidencias. Revisa tu conexión e inténtalo de nuevo.');
  }
  return isPackages
    ? t('No pudimos cargar los paquetes. Inténtalo de nuevo.')
    : t('No pudimos cargar las incidencias. Inténtalo de nuevo.');
}
